import { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Card, Button, Input } from '@components/ui';
import { useUploadMeeting } from '@hooks';

const ACCEPT = '.docx,.txt,.vtt';
const MAX_BYTES = 5 * 1024 * 1024;

// Local calendar day, not the UTC one: a meeting at 9 p.m. still belongs to today.
const today = () => new Date().toLocaleDateString('en-CA');

export default function MeetingUploadForm({ projectId, disabled, onUploaded }) {
  const fileInput = useRef(null);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('');
  const [meetingDate, setMeetingDate] = useState(today);
  const [error, setError] = useState('');
  const upload = useUploadMeeting();

  const handleFile = (e) => {
    const selected = e.target.files?.[0] || null;
    setError('');
    if (selected && selected.size > MAX_BYTES) {
      setError('El archivo supera los 5 MB');
      e.target.value = '';
      return;
    }
    setFile(selected);
    if (selected && !title) setTitle(selected.name.replace(/\.[^.]+$/, ''));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!file) return setError('Selecciona la transcripción');
    if (!meetingDate) return setError('Indica la fecha de la reunión');
    setError('');
    try {
      const result = await upload.mutateAsync({ projectId, file, title: title.trim(), meetingDate });
      setFile(null);
      setTitle('');
      setMeetingDate(today());
      if (fileInput.current) fileInput.current.value = '';
      onUploaded(result.data);
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <Card>
      <form onSubmit={handleSubmit} className="space-y-3">
        <div>
          <h3 className="font-semibold">Subir reunión</h3>
          <p className="text-xs text-slate-500">Transcripción en .docx, .txt o .vtt (Teams, Zoom, Meet). Máx. 5 MB.</p>
        </div>

        <input
          ref={fileInput}
          type="file"
          accept={ACCEPT}
          onChange={handleFile}
          disabled={disabled || upload.isPending}
          className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-lg file:border-0 file:bg-indigo-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-indigo-700 hover:file:bg-indigo-100"
        />

        <Input label="Título" name="meeting-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Ej. Seguimiento semanal" disabled={disabled} />

        <Input
          label="Fecha de la reunión"
          type="date"
          name="meeting-date"
          value={meetingDate}
          onChange={(e) => setMeetingDate(e.target.value)}
          required
          disabled={disabled}
        />
        <p className="text-xs text-slate-500 -mt-1">Se usa para calcular fechas como «el viernes» o «la semana que viene».</p>

        {error && <p className="text-xs text-red-600">{error}</p>}

        <Button type="submit" className="w-full" loading={upload.isPending} disabled={disabled || !file || upload.isPending}>
          Generar borradores con IA
        </Button>
      </form>
    </Card>
  );
}

MeetingUploadForm.propTypes = {
  projectId: PropTypes.number.isRequired,
  disabled: PropTypes.bool,
  onUploaded: PropTypes.func.isRequired,
};
